import { useState, useEffect, useMemo, useCallback } from 'react';
import { getWell } from '../lib/dataSource';

const PRICE_PER_BBL = 2;
// A well needs at least this many days of history before its loss/day is
// considered reliable. Below this, a handful of hauls over a day or two would
// produce a wildly inflated (and misleading) daily rate, so it is excluded from
// the ranking and the field-wide daily estimate.
const MIN_LIFE_DAYS_FOR_RATE = 14;
// Haul tickets are billed in tiers. Most hauls are ~140 bbl (single load);
// larger hauls are ~280 bbl (double load). Snap each well's assumed ticket
// to whichever tier its average haul is closest to.
const TICKET_TIERS = [140, 280];

function nearestTicketTier(avgBbl, tiers) {
  let best = tiers[0];
  let bestDist = Infinity;
  for (const t of tiers) {
    const d = Math.abs(avgBbl - t);
    if (d < bestDist) { bestDist = d; best = t; }
  }
  return best;
}

function mean(arr) {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

function stdDev(arr) {
  if (arr.length < 2) return 0;
  const m = mean(arr);
  const variance = arr.reduce((a, b) => a + (b - m) * (b - m), 0) / (arr.length - 1);
  return Math.sqrt(variance);
}

function fmt(n, d = 1) {
  if (n == null || !isFinite(n)) return '—';
  return n.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
}

function fmtMoney(n) {
  if (n == null || !isFinite(n)) return '—';
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

// NOTE: haul_volume_ft is stored in INCHES of tank level for barrel conversion
// (matching HaulTable, which converts with bblPerIn directly), despite the _ft
// suffix. So barrels = volume * bblPerIn, NOT bblPerIn * 12.
export default function AllWellsPage({ wellList, bblPerIn, minHaulFt, deact }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('moneyPerDay');
  const [sortDir, setSortDir] = useState('desc');
  // Dedicated min-haul filter for this section. Hauls smaller than this are
  // excluded from every well's stats below. Starts at the sidebar value.
  const [minFt, setMinFt] = useState(minHaulFt);
  // Upper bound: hauls larger than this are excluded too (0 = no upper limit).
  // Lets the user focus on a size band and drop outsized/merged hauls.
  const [maxFt, setMaxFt] = useState(0);
  // Bin width (bbl) for the fleet haul-size histogram. 0 = auto.
  const [binWidth, setBinWidth] = useState(0);

  const bblPerUnit = bblPerIn;

  useEffect(() => {
    if (!wellList || wellList.length === 0) return;
    let cancelled = false;
    setLoading(true);
    setRows([]);
    setLoaded(0);

    const results = [];
    let done = 0;

    const fetchOne = (w) =>
      getWell(w.deviceId)
        .then(data => {
          const tanks = data.tanks || {};
          let vols = [];
          let minTs = null;
          let maxTs = null;
          for (const [tankKey, tank] of Object.entries(tanks)) {
            for (const h of tank.hauls || []) {
              const key = `${w.deviceId}|${tankKey}|${h.drop_start}`;
              if (deact && deact.isHaulOff(key)) continue; // skip bad readings
              vols.push(h.haul_volume_ft);
              // Track the well's active life span from haul timestamps.
              const ts = new Date(h.drop_start).getTime();
              if (isFinite(ts)) {
                if (minTs == null || ts < minTs) minTs = ts;
                if (maxTs == null || ts > maxTs) maxTs = ts;
              }
            }
          }
          results.push({
            deviceId: w.deviceId,
            wellName: w.wellName,
            volsFt: vols,
            firstTs: minTs,
            lastTs: maxTs,
          });
        })
        .catch(() => {
          results.push({ deviceId: w.deviceId, wellName: w.wellName, volsFt: [], firstTs: null, lastTs: null });
        })
        .finally(() => {
          done += 1;
          if (!cancelled) setLoaded(done);
        });

    // Fetch with limited concurrency
    const CONC = 6;
    let idx = 0;
    const worker = () => {
      if (idx >= wellList.length) return Promise.resolve();
      const w = wellList[idx++];
      return fetchOne(w).then(worker);
    };
    Promise.all(Array.from({ length: Math.min(CONC, wellList.length) }, worker))
      .then(() => {
        if (!cancelled) {
          setRows(results);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [wellList, deact && deact.deactivatedHauls]);

  const computed = useMemo(() => {
    return rows
      .filter(r => !(deact && deact.isWellOff(r.deviceId)))
      .map(r => {
      const vols = r.volsFt.filter(v => v >= minFt && (maxFt <= 0 || v <= maxFt));
      const n = vols.length;
      const avgFt = mean(vols);
      const sdFt = stdDev(vols);
      const avgBbl = avgFt * bblPerUnit;
      const sdBbl = sdFt * bblPerUnit;
      const totalBbl = mean(vols) * n * bblPerUnit;
      // Overpayment: billed ticket charge vs actual detected avg haul.
      // Ticket snaps to the nearest tier (140 or 280) for this well's avg haul.
      const ticketBbl = n > 0 ? nearestTicketTier(avgBbl, TICKET_TIERS) : 0;
      const deltaBbl = ticketBbl - avgBbl;        // bbl overpaid per haul (if positive)
      const overpaidBbl = deltaBbl * n;           // total over life of well
      const moneyOverpaid = overpaidBbl * PRICE_PER_BBL;
      // What the well is actually billed over its life (ticket tier × hauls).
      const billedBbl = ticketBbl * n;
      const moneyBilled = billedBbl * PRICE_PER_BBL;
      // Overpay % = how much of the bill is wasted (overpaid / billed).
      const overpayPct = moneyBilled > 0 ? (moneyOverpaid / moneyBilled) * 100 : 0;
      // Active life of the well: span from its first to last haul. Loss/day
      // normalises the overpayment by how long the well has been producing, so
      // wells with different lifespans can be compared and the field-wide daily
      // loss can be estimated. At least 1 day to avoid divide-by-zero.
      const lifeDays = (r.firstTs != null && r.lastTs != null && r.lastTs > r.firstTs)
        ? (r.lastTs - r.firstTs) / 86400000
        : (n > 0 ? 1 : 0);
      // Only trust a daily rate once the well has enough history; otherwise a
      // tiny denominator inflates it. rateReliable gates the field-wide sum.
      const rateReliable = lifeDays >= MIN_LIFE_DAYS_FOR_RATE;
      const moneyPerDay = rateReliable ? moneyOverpaid / lifeDays : null;
      const overpaidBblPerDay = rateReliable ? overpaidBbl / lifeDays : null;
      const moneyBilledPerDay = rateReliable ? moneyBilled / lifeDays : null;
      return {
        ...r,
        n,
        avgFt, sdFt,
        avgBbl, sdBbl,
        totalBbl,
        ticketBbl,
        deltaBbl,
        overpaidBbl,
        moneyOverpaid,
        billedBbl,
        moneyBilled,
        overpayPct,
        lifeDays,
        rateReliable,
        moneyPerDay,
        overpaidBblPerDay,
        moneyBilledPerDay,
      };
    });
  }, [rows, bblPerUnit, minFt, maxFt, deact]);

  const sorted = useMemo(() => {
    const arr = [...computed];
    arr.sort((a, b) => {
      let va = a[sortKey], vb = b[sortKey];
      if (sortKey === 'wellName') {
        va = va.toLowerCase(); vb = vb.toLowerCase();
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      va = va ?? 0; vb = vb ?? 0;
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return arr;
  }, [computed, sortKey, sortDir]);

  const totals = useMemo(() => {
    return computed.reduce((acc, r) => {
      acc.n += r.n;
      acc.totalBbl += r.totalBbl;
      acc.overpaidBbl += r.overpaidBbl;
      acc.moneyOverpaid += r.moneyOverpaid;
      acc.moneyBilled += r.moneyBilled;
      // Field-wide daily loss = sum of each well's own loss/day (each well
      // normalised by its own active life), so it estimates the current
      // combined loss rate across all active wells. Only wells with enough
      // history contribute a reliable daily rate.
      if (r.moneyPerDay != null) {
        acc.moneyPerDay += r.moneyPerDay;
        acc.overpaidBblPerDay += r.overpaidBblPerDay;
        acc.rateWells += 1;
      }
      return acc;
    }, { n: 0, totalBbl: 0, overpaidBbl: 0, moneyOverpaid: 0, moneyBilled: 0, moneyPerDay: 0, overpaidBblPerDay: 0, rateWells: 0 });
  }, [computed]);

  // Field-wide overpay % (waste as a share of the total bill).
  const fieldOverpayPct = totals.moneyBilled > 0
    ? (totals.moneyOverpaid / totals.moneyBilled) * 100
    : 0;

  // Best / worst wells and the overpay-% distribution (across reliable wells so
  // short-life noise does not dominate).
  const { worst, best, dist, distMax } = useMemo(() => {
    const elig = computed.filter(r => r.n > 0 && r.rateReliable);
    const byPct = [...elig].sort((a, b) => b.overpayPct - a.overpayPct);
    const worst = byPct.slice(0, 5);
    const best = byPct.slice(-5).reverse(); // lowest overpay %, best first
    // Histogram of overpay % in 10-point buckets: 0-10, 10-20, ... 90-100.
    const buckets = Array.from({ length: 10 }, (_, i) => ({
      label: `${i * 10}–${i * 10 + 10}%`,
      lo: i * 10,
      count: 0,
    }));
    for (const r of elig) {
      let idx = Math.floor(Math.max(0, Math.min(99.999, r.overpayPct)) / 10);
      if (idx < 0) idx = 0;
      if (idx > 9) idx = 9;
      buckets[idx].count += 1;
    }
    const distMax = Math.max(1, ...buckets.map(b => b.count));
    return { worst, best, dist: buckets, distMax };
  }, [computed]);

  // Fleet-wide haul-size distribution: pool EVERY haul across ALL wells (in bbl,
  // respecting the min/max filters) so the field's overall haul-size spread and
  // the ticket tiers (clusters near 140 / 280 bbl) are visible in one view.
  const haulDist = useMemo(() => {
    const all = [];
    for (const r of computed) {
      for (const v of r.volsFt) {
        if (v >= minFt && (maxFt <= 0 || v <= maxFt)) all.push(v * bblPerUnit);
      }
    }
    if (all.length === 0) return { bins: [], binMax: 0, total: 0, hi: 0, binW: 0 };
    const hiRaw = Math.max(...all);
    let binW;
    if (binWidth > 0) {
      // User-chosen bin width (bbl), from 1 upwards.
      binW = binWidth;
    } else {
      // Auto: round the upper edge to a clean multiple of 20 bbl, ~24 bins.
      const hiAuto = Math.max(20, Math.ceil(hiRaw / 20) * 20);
      const targetBins = 24;
      binW = Math.max(10, Math.ceil(hiAuto / targetBins / 10) * 10);
    }
    const hi = Math.max(binW, Math.ceil(hiRaw / binW) * binW);
    const nbins = Math.ceil(hi / binW);
    const bins = Array.from({ length: nbins }, (_, i) => ({
      lo: i * binW,
      hi: (i + 1) * binW,
      count: 0,
    }));
    for (const b of all) {
      let idx = Math.floor(b / binW);
      if (idx < 0) idx = 0;
      if (idx >= nbins) idx = nbins - 1;
      bins[idx].count += 1;
    }
    const binMax = Math.max(1, ...bins.map(b => b.count));
    return { bins, binMax, total: all.length, hi, binW };
  }, [computed, minFt, maxFt, bblPerUnit, binWidth]);

  const handleSort = useCallback((key) => {
    setSortKey(prev => {
      if (prev === key) {
        setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortDir(key === 'wellName' ? 'asc' : 'desc');
      return key;
    });
  }, []);

  const arrow = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  if (bblPerIn <= 0) {
    return (
      <div className="allwells-page">
        <div className="no-data">Set tank geometry (diameter &gt; 0) to compute barrel volumes.</div>
      </div>
    );
  }

  return (
    <div className="allwells-page">
      <div className="allwells-header">
        <h2>All Wells — Haul Consistency &amp; Estimated Loss</h2>
        <div className="allwells-sub">
          Overpaid = (ticket charge − avg haul) × hauls × ${PRICE_PER_BBL}/bbl ·
          Ticket charge snaps to nearest tier ({TICKET_TIERS.join(' or ')} bbl) per well ·
          Loss / day = a well's total overpaid ÷ its active life (first→last haul) ·
          Field loss / day = sum of all wells' loss/day ·
          {bblPerUnit.toFixed(2)} bbl per unit of level drop
        </div>
        <div className="allwells-filters">
        <div className="allwells-filter">
          <label htmlFor="aw-minhaul">Exclude hauls below</label>
          <input
            id="aw-minhaul"
            type="range"
            min={0}
            max={40}
            step={0.5}
            value={minFt}
            onChange={e => setMinFt(Number(e.target.value))}
          />
          <span className="allwells-filter-val">
            {minFt} in{bblPerUnit > 0 ? ` (${(minFt * bblPerUnit).toFixed(0)} bbl)` : ''}
          </span>
          <input
            type="number"
            className="allwells-filter-num"
            min={0}
            max={200}
            step={0.5}
            value={minFt}
            onChange={e => setMinFt(Math.max(0, Number(e.target.value) || 0))}
            title="Minimum haul size (inches of level drop) to include"
          />
        </div>
        <div className="allwells-filter">
          <label htmlFor="aw-maxhaul">Exclude hauls above</label>
          <input
            id="aw-maxhaul"
            type="range"
            min={0}
            max={120}
            step={0.5}
            value={maxFt}
            onChange={e => setMaxFt(Number(e.target.value))}
          />
          <span className="allwells-filter-val">
            {maxFt <= 0
              ? 'no limit'
              : `${maxFt} in${bblPerUnit > 0 ? ` (${(maxFt * bblPerUnit).toFixed(0)} bbl)` : ''}`}
          </span>
          <input
            type="number"
            className="allwells-filter-num"
            min={0}
            max={500}
            step={0.5}
            value={maxFt}
            onChange={e => setMaxFt(Math.max(0, Number(e.target.value) || 0))}
            title="Maximum haul size to include (0 = no upper limit)"
          />
        </div>
        </div>
      </div>

      {loading && (
        <div className="partial-banner">
          <div className="loading-spinner small" />
          Loading wells... {loaded}/{wellList.length}
        </div>
      )}

      <div className="allwells-summary">
        <div className="aw-stat">
          <div className="aw-stat-label">Wells</div>
          <div className="aw-stat-value">{computed.length}</div>
        </div>
        <div className="aw-stat">
          <div className="aw-stat-label">Total hauls</div>
          <div className="aw-stat-value">{fmt(totals.n, 0)}</div>
        </div>
        <div className="aw-stat highlight">
          <div className="aw-stat-label">Total overpaid (tiered ticket)</div>
          <div className="aw-stat-value">{fmtMoney(totals.moneyOverpaid)}</div>
          <div className="aw-stat-sub">of {fmtMoney(totals.moneyBilled)} billed</div>
        </div>
        <div className="aw-stat highlight">
          <div className="aw-stat-label">Field overpay %</div>
          <div className="aw-stat-value">{fmt(fieldOverpayPct, 1)}%</div>
          <div className="aw-stat-sub">share of total bill wasted</div>
        </div>
        <div className="aw-stat highlight">
          <div className="aw-stat-label">Field loss / day</div>
          <div className="aw-stat-value">{fmtMoney(totals.moneyPerDay)}</div>
          <div className="aw-stat-sub">
            {fmt(totals.overpaidBblPerDay, 0)} bbl/day · {totals.rateWells} wells ≥ {MIN_LIFE_DAYS_FOR_RATE}d
          </div>
        </div>
      </div>

      {/* Best / worst wells + overpay-% distribution (reliable wells only). */}
      {(worst.length > 0 || best.length > 0) && (
        <div className="aw-kpi-grid">
          <div className="aw-kpi-card worst">
            <h3>Worst overpayers</h3>
            <div className="aw-kpi-sub">Highest % of bill wasted</div>
            <ol className="aw-kpi-list">
              {worst.map(r => (
                <li key={r.deviceId}>
                  <span className="aw-kpi-name" title={r.wellName}>{r.wellName}</span>
                  <span className="aw-kpi-vals">
                    <b className="delta-pos">{fmt(r.overpayPct, 1)}%</b>
                    <span className="aw-kpi-day">{fmtMoney(r.moneyPerDay)}/day</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>

          <div className="aw-kpi-card best">
            <h3>Best (lowest waste)</h3>
            <div className="aw-kpi-sub">Closest to fair ticket</div>
            <ol className="aw-kpi-list">
              {best.map(r => (
                <li key={r.deviceId}>
                  <span className="aw-kpi-name" title={r.wellName}>{r.wellName}</span>
                  <span className="aw-kpi-vals">
                    <b className="delta-neg">{fmt(r.overpayPct, 1)}%</b>
                    <span className="aw-kpi-day">{fmtMoney(r.moneyPerDay)}/day</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>

          <div className="aw-kpi-card dist">
            <h3>Overpay % distribution</h3>
            <div className="aw-kpi-sub">Wells by share of bill wasted</div>
            <div className="aw-hist">
              {dist.map(b => (
                <div className="aw-hist-col" key={b.label} title={`${b.label}: ${b.count} well${b.count === 1 ? '' : 's'}`}>
                  <div className="aw-hist-count">{b.count || ''}</div>
                  <div className="aw-hist-bar-wrap">
                    <div
                      className="aw-hist-bar"
                      style={{ height: `${(b.count / distMax) * 100}%` }}
                    />
                  </div>
                  <div className="aw-hist-label">{b.lo}</div>
                </div>
              ))}
            </div>
            <div className="aw-kpi-sub" style={{ textAlign: 'center', marginTop: 2 }}>overpay % bucket (lower bound)</div>
          </div>
        </div>
      )}

      <table className="allwells-table">
        <thead>
          <tr>
            <th className="sortable left" onClick={() => handleSort('wellName')}>Well{arrow('wellName')}</th>
            <th className="sortable" onClick={() => handleSort('n')}>Hauls{arrow('n')}</th>
            <th className="sortable" onClick={() => handleSort('avgBbl')}>Avg haul (bbl){arrow('avgBbl')}</th>
            <th className="sortable" onClick={() => handleSort('sdBbl')}>Std dev (bbl){arrow('sdBbl')}</th>
            <th className="sortable" onClick={() => handleSort('ticketBbl')}>Ticket (bbl){arrow('ticketBbl')}</th>
            <th className="sortable" onClick={() => handleSort('deltaBbl')}>Delta/haul (bbl){arrow('deltaBbl')}</th>
            <th className="sortable" onClick={() => handleSort('overpaidBbl')}>Overpaid bbls{arrow('overpaidBbl')}</th>
            <th className="sortable" onClick={() => handleSort('moneyOverpaid')}>Money overpaid{arrow('moneyOverpaid')}</th>
            <th className="sortable" onClick={() => handleSort('overpayPct')}>Overpay %{arrow('overpayPct')}</th>
            <th className="sortable" onClick={() => handleSort('lifeDays')}>Life (days){arrow('lifeDays')}</th>
            <th className="sortable" onClick={() => handleSort('moneyPerDay')}>Loss / day{arrow('moneyPerDay')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(r => (
            <tr key={r.deviceId}>
              <td className="left">{r.wellName}</td>
              <td>{fmt(r.n, 0)}</td>
              <td>{fmt(r.avgBbl, 1)}</td>
              <td>{fmt(r.sdBbl, 1)}</td>
              <td>{fmt(r.ticketBbl, 0)}</td>
              <td className={r.deltaBbl >= 0 ? 'delta-pos' : 'delta-neg'}>
                {r.deltaBbl >= 0 ? '+' : ''}{fmt(r.deltaBbl, 1)}
              </td>
              <td>{fmt(r.overpaidBbl, 0)}</td>
              <td className="money">{fmtMoney(r.moneyOverpaid)}</td>
              <td className={r.overpayPct >= 40 ? 'delta-pos' : ''} title="Overpaid as a share of what the well is billed">
                {fmt(r.overpayPct, 1)}%
              </td>
              <td>{fmt(r.lifeDays, 0)}</td>
              <td className="money" title={r.moneyPerDay == null ? `Needs ≥ ${MIN_LIFE_DAYS_FOR_RATE} days of history for a reliable daily rate` : ''}>
                {r.moneyPerDay == null ? '—' : fmtMoney(r.moneyPerDay)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Fleet-wide haul-size distribution across every well. */}
      {haulDist.bins.length > 0 && (
        <div className="aw-fleetdist">
          <div className="aw-fleetdist-head">
            <h3>Fleet haul-size distribution</h3>
            <label className="aw-binsel" title="Histogram bin width in barrels">
              Bin size
              <select value={binWidth} onChange={e => setBinWidth(Number(e.target.value))}>
                <option value={0}>Auto</option>
                <option value={1}>1 bbl</option>
                <option value={2}>2 bbl</option>
                <option value={5}>5 bbl</option>
                <option value={10}>10 bbl</option>
                <option value={20}>20 bbl</option>
                <option value={25}>25 bbl</option>
                <option value={50}>50 bbl</option>
                <option value={100}>100 bbl</option>
              </select>
            </label>
          </div>
          <div className="aw-kpi-sub">
            All {fmt(haulDist.total, 0)} hauls across {computed.filter(r => r.n > 0).length} wells (bbl){' '}
            · {haulDist.binW} bbl bins · {haulDist.bins.length} bins · ticket tiers marked
          </div>
          <div className="aw-fleethist">
            {haulDist.bins.map((b, i) => {
              // Highlight bins that straddle a ticket tier.
              const nearTier = TICKET_TIERS.some(t => t >= b.lo && t < b.hi);
              // With many bins, thin out per-bar counts/labels so they don't
              // collide (aim for ~14 x-axis labels).
              const many = haulDist.bins.length > 40;
              const labelStep = Math.max(1, Math.ceil(haulDist.bins.length / 14));
              const showLabel = i % labelStep === 0;
              return (
                <div
                  className="aw-hist-col"
                  key={b.lo}
                  title={`${b.lo}–${b.hi} bbl: ${b.count} haul${b.count === 1 ? '' : 's'}`}
                >
                  <div className="aw-hist-count">{many ? '' : (b.count || '')}</div>
                  <div className="aw-hist-bar-wrap">
                    <div
                      className={'aw-hist-bar' + (nearTier ? ' tier' : '')}
                      style={{ height: `${(b.count / haulDist.binMax) * 100}%` }}
                    />
                  </div>
                  <div className="aw-hist-label">{showLabel ? b.lo : ''}</div>
                </div>
              );
            })}
          </div>
          <div className="aw-fleethist-legend">
            <span className="aw-tier-swatch" /> tier bucket ({TICKET_TIERS.join(' / ')} bbl)
            <span style={{ marginLeft: 14 }}>x-axis: haul size (bbl, bin lower bound)</span>
          </div>
        </div>
      )}
    </div>
  );
}
